import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { Know } from 'interfaces/Know'
import {
    AnalyticsEventKnowConversionDialogActionButtonClick,
    AnalyticsEventKnowConversionDialogButtonClick,
    AnalyticsEventKnowConversionLike,
    AnalyticsEventKnowConversionSave,
    AnalyticsEventKnowConversionShare,
    AnalyticsEventKnowImagePageClick,
} from 'lib/constants/AnalyticsEventType'
import _useTranslation from 'lib/hooks/_useTranslation'
import { trackEvent } from 'lib/tracking/analytics-service'
import { useState, useEffect } from 'react'
import { Body2, ButtonText } from 'stories/elements/Typography/Text'
import ReportDialog from 'components/feature/Reports/ReportDialog'
import { ReportType } from 'interfaces/UserReport'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import { useToggleState } from 'lib/hooks/useToggleState'
import WebappConversionDialog from '../../../webapp/WebappConversionDialog'
import LikeIcon from 'assets/ai-enhanced/heart.svg'
import ShareIcon from 'assets/ai-enhanced/share.svg'
import SaveIcon from 'assets/ai-enhanced/save.svg'
import ReportIcon from 'assets/ai-enhanced/report.svg'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import { scrollToId } from 'lib/features/scroll'
import Stack from 'components/elements/Stack'
import NextImage from 'components/elements/NextImage'

const Container = styled.aside<{ isDarkTheme?: boolean; showSeoPages?: boolean }>`
    right: 0px;
    position: fixed;
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.darkThemePrimary : theme.colors.white)};
    border-radius: ${theme.borderRadius.normal} 0 0 ${theme.borderRadius.normal};
    box-shadow: 0px 4px 16px 0px #0000001f;
    top: 125px;
    padding: 25px 25px 25px 40px;
    transition: all 0.5s;
    height: fit-content;
    width: fit-content;
    z-index: ${theme.zIndex.FAB};
    .content {
        display: grid;
        gap: 35px;
        p {
            cursor: pointer;
        }
    }

    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  * {
                      color: white;
                      svg path {
                          stroke: white;
                      }
                  }
              `
            : ''}

    p:hover {
        color: ${theme.colors.deepSeaBlue};
    }
    .actions {
        display: grid;
        gap: 25px;
    }
    ${({ showSeoPages }) =>
        showSeoPages
            ? css`
                  @media (min-width: 1051px) {
                      img {
                          border-radius: ${theme.borderRadius.small};
                          border: 1px solid ${theme.colors.lightGray};
                          box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.05);
                      }
                      top: 70px;
                      position: fixed;
                      background-color: transparent;
                      box-shadow: none;
                      height: calc(100vh - var(--navbar-height));
                      overflow-y: scroll;
                      padding-bottom: 50px;
                      padding-right: 20px;
                  }
              `
            : ''}
    @media (max-width: 1050px) {
        bottom: 0;
        top: unset;
        width: 100%;
        padding: 10px;
        border-radius: 0px;

        .actions {
            display: flex;
            justify-content: space-evenly;
            gap: 0px;
        }
    }
`

const ActionWrapper = styled.div<{ isReport?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    flex-direction: column;
    cursor: pointer;
    .box {
        border-radius: ${theme.borderRadius.normal};
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        box-shadow: 0px 4px 16px 0px #0000001f;
    }
    :hover {
        p {
            color: ${({ isReport }) => (isReport ? theme.colors.negativeSignal : theme.colors.deepSeaBlue)};
        }
    }
    p {
        font-size: 13px;
    }
    @media (max-width: 1050px) {
        flex-direction: row;
        .box {
            box-shadow: none;
        }
    }
`

const ImageContainer = styled.div<{
    isActive: boolean
    isDarkTheme?: boolean
}>`
    position: relative;
    overflow: visible;
    user-select: none;
    -webkit-touch-callout: none;
    pointer-events: none;
    ${({ isActive, isDarkTheme }) =>
        isActive
            ? css`
                  &::after {
                      content: '';
                      position: absolute;
                      z-index: 0;
                      background-color: ${isDarkTheme ? theme.colors.darkThemePrimary : theme.colors.knowunityBlue};
                      overflow: visible;
                      top: unset;
                      bottom: -2px;
                      width: 85%;
                      height: 7px;
                      right: 10px;
                      border-radius: 0 0 ${theme.borderRadius.small} ${theme.borderRadius.small};
                  }
              `
            : ''}
    img {
        z-index: 1;
    }
`

const ImageWrapper = styled(NextImage)`
    width: 100%;
    height: 100%;
    z-index: 1;
`

interface Props {
    know: Know
    currentActivePage: number | null
    settings: {
        hasSimilarKnows?: boolean
        hasFlashcards?: boolean
        isMobile?: boolean
        isDarkTheme?: boolean
    }
}

const AIEnhancedPNGSidebar = ({
    know,
    currentActivePage,
    settings: { hasFlashcards, hasSimilarKnows, isMobile, isDarkTheme },
}: Props) => {
    const [conversionReferrerScreen, setConversionReferrerScreen] = useState<string | undefined>()
    const [showConversionDialog, toggleConversionDialog] = useToggleState(false)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')
    const [conversionDialogHeadingCopy, setConversionDialogHeading] = useState<string>(
        t('knows/conversionDialogExperimentVariantHeading')
    )
    const referrerScreen = 'knowPreviewSidebar'

    const seoPages = know.knowDocumentPages?.slice(0, 10)

    const showSeoPages = !!seoPages?.length && seoPages.length > 2 && !isMobile

    const handleDynamicCopyChange = (source: 'Like' | 'Comment' | 'Save' | 'Share') => {
        setConversionDialogHeading(t(`knows/conversionDialogExperimentVariantHeading${source}Variant`))
    }

    const onKnowActionClicked = (type: 'Like' | 'Comment' | 'Save' | 'Share') =>
        setConversionReferrerScreen(`knowConversion${type}`)

    const conversionDialogButtonClick = (source?: 'Like' | 'Comment' | 'Save' | 'Share') => {
        trackEvent(AnalyticsEventKnowConversionDialogButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })

        toggleConversionDialog()
        if (source) {
            handleDynamicCopyChange(source)
            onKnowActionClicked(source)
        }
    }

    const saveConversionHandleClick = () => {
        trackEvent(AnalyticsEventKnowConversionSave, { know, referrerScreen })
        conversionDialogButtonClick('Save')
    }

    const likeConversionHandleClick = () => {
        trackEvent(AnalyticsEventKnowConversionLike, { know, referrerScreen })
        conversionDialogButtonClick('Like')
    }

    const shareConversionHandleClick = () => {
        trackEvent(AnalyticsEventKnowConversionShare, { know, referrerScreen })
        conversionDialogButtonClick('Share')
    }

    const closeConversionDialog = () => {
        toggleConversionDialog()
        setTimeout(() => {
            setConversionDialogHeading(t(`knows/conversionDialogExperimentVariantHeading`))
        }, 500)
    }

    const handleConversionDialogActionButtonClick = (platform: 'android' | 'ios') => {
        trackEvent(AnalyticsEventKnowConversionDialogActionButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })
        redirectToAppDownloadUrlAsync(platform, 'knowSeoPage', appDeepLinkKnow(know.uuid))
    }

    useEffect(() => {
        if (!currentActivePage || !showSeoPages) return

        const navigationContainer = document.getElementById('navigation-pages')
        const activePreview = navigationContainer?.querySelector(`[data-page="${currentActivePage}"]`)

        if (activePreview) {
            activePreview.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }, [currentActivePage, showSeoPages])

    return (
        <Container isDarkTheme={isDarkTheme} showSeoPages={showSeoPages}>
            {showSeoPages ? (
                <>
                    <Stack gutter={8} id="navigation-pages">
                        {seoPages.map((page) => (
                            <a
                                href={`#page-${page.pageNumber}`}
                                key={page.uuid}
                                data-page={page.pageNumber}
                                onClick={(e) => {
                                    e.preventDefault()
                                    trackEvent(AnalyticsEventKnowImagePageClick, {
                                        know,
                                        value: page.pageNumber.toString(),
                                        valueId: page.pageNumber,
                                    })
                                    scrollToId(`page-${page.pageNumber - 1}`)
                                }}
                            >
                                <ImageContainer
                                    key={page.uuid}
                                    isActive={page.pageNumber === currentActivePage}
                                    onContextMenu={(e) => e.preventDefault()}
                                    isDarkTheme={isDarkTheme}
                                >
                                    <ImageWrapper src={page.imageUrl} alt={`${page.pageNumber}`} width={120} height={160} />
                                </ImageContainer>
                            </a>
                        ))}
                    </Stack>
                </>
            ) : (
                <>
                    <div className="content">
                        {!isMobile ? (
                            <>
                                <Body2
                                    onClick={() => scrollToId('hero')}
                                    fontFamily="var(--font-inter)"
                                    fontWeight={500}
                                    color={theme.colors.basicContentStateDisabled}
                                >
                                    {t('knows/introductionSectionLabel')}
                                </Body2>

                                <Body2
                                    onClick={() => scrollToId('know-content')}
                                    fontFamily="var(--font-inter)"
                                    fontWeight={500}
                                    color={theme.colors.basicContentStateDisabled}
                                >
                                    {t('knows/uploadBannerKnow')}
                                </Body2>

                                <Body2
                                    onClick={() => scrollToId('quiz')}
                                    fontFamily="var(--font-inter)"
                                    fontWeight={500}
                                    color={theme.colors.basicContentStateDisabled}
                                >
                                    {t('knows/quizSectionLabel')}
                                </Body2>

                                {hasSimilarKnows ? (
                                    <Body2
                                        onClick={() => scrollToId('similar-knows')}
                                        fontFamily="var(--font-inter)"
                                        fontWeight={500}
                                        color={theme.colors.basicContentStateDisabled}
                                    >
                                        {t('knows/similarKnowsHeading')}
                                    </Body2>
                                ) : null}
                            </>
                        ) : null}
                        <div className="actions">
                            <ActionWrapper onClick={saveConversionHandleClick}>
                                <div className="box">
                                    <SaveIcon />
                                </div>
                                {!isMobile ? (
                                    <ButtonText
                                        fontFamily="var(--font-inter)"
                                        fontWeight={400}
                                        color={theme.colors.actionGray}
                                    >
                                        {tCommon('common/save')}
                                    </ButtonText>
                                ) : null}
                            </ActionWrapper>
                            <ActionWrapper onClick={likeConversionHandleClick}>
                                <div className="box">
                                    <LikeIcon />
                                </div>
                                <ButtonText fontFamily="var(--font-inter)" fontWeight={400} color={theme.colors.actionGray}>
                                    {know.likes}
                                </ButtonText>
                            </ActionWrapper>
                            <ActionWrapper onClick={shareConversionHandleClick}>
                                <div className="box">
                                    <ShareIcon />
                                </div>
                                {!isMobile ? (
                                    <ButtonText
                                        fontFamily="var(--font-inter)"
                                        fontWeight={400}
                                        color={theme.colors.actionGray}
                                    >
                                        {tCommon('common/share')}
                                    </ButtonText>
                                ) : null}
                            </ActionWrapper>
                            <ReportDialog
                                reportType={ReportType.know}
                                reportedUserUuid={know.knower.user.uuid}
                                know={know}
                                icon={
                                    <ActionWrapper isReport>
                                        <div className="box">
                                            <ReportIcon />
                                        </div>
                                        {!isMobile ? (
                                            <ButtonText
                                                fontFamily="var(--font-inter)"
                                                fontWeight={400}
                                                color={theme.colors.actionGray}
                                            >
                                                {tCommon('common/reportButtonLabel')}
                                            </ButtonText>
                                        ) : null}
                                    </ActionWrapper>
                                }
                            />
                        </div>
                    </div>
                    {know && showConversionDialog ? (
                        <WebappConversionDialog
                            open={showConversionDialog}
                            title={conversionDialogHeadingCopy}
                            handleClose={closeConversionDialog}
                            onButtonClick={handleConversionDialogActionButtonClick}
                            downloadDeepLinkPath={appDeepLinkKnow(know.uuid)}
                            know={know}
                            referrerScreen={conversionReferrerScreen}
                        />
                    ) : null}
                </>
            )}
        </Container>
    )
}

export default AIEnhancedPNGSidebar
